<template>
  <Teleport to="body">
    <TransitionRoot appear :show="modal.isOpen.value" as="template">
      <Dialog as="div" @close="modal.close()" class="relative z-[9999]">
        <!-- Backdrop -->
        <TransitionChild
          as="template"
          enter="duration-200 ease-out"
          enter-from="opacity-0"
          enter-to="opacity-100"
          leave="duration-150 ease-in"
          leave-from="opacity-100"
          leave-to="opacity-0">
          <div class="fixed inset-0 bg-black/50 backdrop-blur-sm" />
        </TransitionChild>

        <div class="fixed inset-0 overflow-y-auto !z-[10000]">
          <div class="flex min-h-full items-center justify-center p-4">
            <TransitionChild
              as="template"
              enter="duration-200 ease-out"
              enter-from="opacity-0 scale-95"
              enter-to="opacity-100 scale-100"
              leave="duration-150 ease-in"
              leave-from="opacity-100 scale-100"
              leave-to="opacity-0 scale-95">
              <DialogPanel
                class="w-full max-w-lg transform rounded-2xl bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 shadow-2xl transition-all flex flex-col max-h-[85vh]">
                <!-- Header -->
                <div
                  class="flex flex-wrap items-center justify-between gap-2 px-5 py-4 border-b border-gray-100 dark:border-gray-800 flex-shrink-0">
                  <div class="flex items-center gap-2.5">
                    <span class="material-symbols-outlined text-purple-500 text-xl">mic</span>
                    <h2 class="font-semibold text-gray-900 dark:text-white text-base">
                      {{ chrome[activeLang].title }}
                    </h2>
                  </div>

                  <div class="flex flex-wrap items-center justify-end gap-2">
                    <!-- Language toggle -->
                    <div
                      class="flex rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden text-xs font-medium">
                      <button
                        v-for="lang in langs"
                        :key="lang.code"
                        @click="activeLang = lang.code"
                        :class="[
                          'px-2.5 py-1.5 transition-colors',
                          activeLang === lang.code
                            ? 'bg-purple-600 text-white'
                            : 'text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800',
                        ]">
                        {{ lang.label }}
                      </button>
                    </div>

                    <button
                      @click="modal.close()"
                      class="p-1.5 rounded-lg text-gray-400 hover:text-gray-700 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors">
                      <span class="material-symbols-outlined text-lg">close</span>
                    </button>
                  </div>
                </div>

                <!-- Body — scrollable -->
                <div class="overflow-y-auto flex-1 px-5 py-4 space-y-5">
                  <section v-for="section in visibleSections" :key="section.key">
                    <!-- Section header -->
                    <div class="flex items-center gap-2 mb-2">
                      <span class="material-symbols-outlined text-base" :class="section.iconColor">{{
                        section.icon
                      }}</span>
                      <h3 class="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                        {{ section.title[activeLang] }}
                      </h3>
                    </div>

                    <!-- Commands -->
                    <div class="space-y-1.5">
                      <div
                        v-for="cmd in section.commands"
                        :key="cmd.example"
                        class="flex items-start gap-3 bg-gray-50 dark:bg-gray-800/60 rounded-lg px-3 py-2">
                        <code
                          class="text-xs text-purple-700 dark:text-purple-300 font-mono leading-relaxed flex-1 whitespace-pre-wrap"
                          >"{{ cmd.example }}"</code
                        >
                        <span class="text-xs text-gray-400 dark:text-gray-500 flex-shrink-0 leading-relaxed">{{
                          notes[cmd.note][activeLang]
                        }}</span>
                      </div>
                    </div>
                  </section>
                </div>

                <!-- Footer -->
                <div
                  class="px-5 py-3 border-t border-gray-100 dark:border-gray-800 flex-shrink-0 flex items-center justify-between">
                  <p class="text-xs text-gray-400 dark:text-gray-500">
                    {{ chrome[activeLang].wake(heyAgent) }}
                  </p>
                  <button
                    @click="modal.close()"
                    class="px-4 py-1.5 text-sm rounded-lg bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300 transition-colors font-medium">
                    {{ chrome[activeLang].close }}
                  </button>
                </div>
              </DialogPanel>
            </TransitionChild>
          </div>
        </div>
      </Dialog>
    </TransitionRoot>
  </Teleport>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { Dialog, DialogPanel, TransitionRoot, TransitionChild } from '@headlessui/vue';
import { heyAgent, brand } from '@/config/brand';
import { useVoiceCommandsModal } from '@/composables/useVoiceCommandsModal';

const modal = useVoiceCommandsModal();
const { locale } = useI18n();

/**
 * The languages this list is written in — exactly the six the app ships and the
 * hub's fast parser understands (see otto `IntentParser`). Adding a seventh here
 * without teaching the parser its words would be a lie: the command would fall
 * through to the LLM (or to nothing) instead of being executed.
 *
 * Every example below was checked against the parser in its own language, so
 * the wording is what the hub actually acts on.
 */
type LangCode = 'en' | 'sl' | 'de' | 'es' | 'fr' | 'it';

const langs: { code: LangCode; label: string }[] = [
  { code: 'en', label: 'EN' },
  { code: 'sl', label: 'SL' },
  { code: 'de', label: 'DE' },
  { code: 'es', label: 'ES' },
  { code: 'fr', label: 'FR' },
  { code: 'it', label: 'IT' },
];

function isLangCode(value: string): value is LangCode {
  return langs.some((lang) => lang.code === value);
}

// Open on the app's own language when it is one of these, otherwise English.
const appLanguage = String(locale.value ?? '')
  .slice(0, 2)
  .toLowerCase();
const activeLang = ref<LangCode>(isLangCode(appLanguage) ? appLanguage : 'en');

type Text = Record<LangCode, string>;

const chrome: Record<LangCode, { title: string; close: string; wake: (agent: string) => string }> = {
  en: {
    title: 'Voice Commands',
    close: 'Close',
    wake: (agent) => `Say "${agent}" to activate voice`,
  },
  sl: {
    title: 'Glasovni ukazi',
    close: 'Zapri',
    wake: (agent) => `Reci "${agent}" za aktivacijo glasu`,
  },
  de: {
    title: 'Sprachbefehle',
    close: 'Schließen',
    wake: (agent) => `Sage "${agent}", um die Stimme zu aktivieren`,
  },
  es: {
    title: 'Comandos de voz',
    close: 'Cerrar',
    wake: (agent) => `Di "${agent}" para activar la voz`,
  },
  fr: {
    title: 'Commandes vocales',
    close: 'Fermer',
    wake: (agent) => `Dis "${agent}" pour activer la voix`,
  },
  it: {
    title: 'Comandi vocali',
    close: 'Chiudi',
    wake: (agent) => `Di "${agent}" per attivare la voce`,
  },
};

/** The right-hand label of a command row. */
const notes: Record<string, Text> = {
  turnOn: {
    en: 'turn on',
    sl: 'vklop',
    de: 'einschalten',
    es: 'encender',
    fr: 'allumer',
    it: 'accendere',
  },
  turnOff: {
    en: 'turn off',
    sl: 'izklop',
    de: 'ausschalten',
    es: 'apagar',
    fr: 'éteindre',
    it: 'spegnere',
  },
  setBrightness: {
    en: 'set brightness',
    sl: 'nastavi svetlost',
    de: 'Helligkeit setzen',
    es: 'ajustar brillo',
    fr: 'régler la luminosité',
    it: 'impostare la luminosità',
  },
  increase: {
    en: 'increase',
    sl: 'povečaj',
    de: 'erhöhen',
    es: 'subir',
    fr: 'augmenter',
    it: 'aumentare',
  },
  decrease: {
    en: 'decrease',
    sl: 'zmanjšaj',
    de: 'verringern',
    es: 'bajar',
    fr: 'baisser',
    it: 'abbassare',
  },
  allRoomLights: {
    en: 'all room lights',
    sl: 'vse luči v sobi',
    de: 'alle Lichter im Raum',
    es: 'todas las luces de la habitación',
    fr: 'toutes les lumières de la pièce',
    it: 'tutte le luci della stanza',
  },
  everyLight: {
    en: 'every light in home',
    sl: 'vse luči v domu',
    de: 'alle Lichter im Haus',
    es: 'todas las luces de la casa',
    fr: 'toutes les lumières de la maison',
    it: 'tutte le luci di casa',
  },
  scene: {
    en: 'execute scene',
    sl: 'izvedi sceno',
    de: 'Szene ausführen',
    es: 'ejecutar escena',
    fr: 'exécuter la scène',
    it: 'eseguire la scena',
  },
  sceneList: {
    en: 'list scenes',
    sl: 'seznam scen',
    de: 'Szenen anzeigen',
    es: 'ver escenas',
    fr: 'lister les scènes',
    it: 'elenco scene',
  },
  navigate: {
    en: 'navigate',
    sl: 'navigacija',
    de: 'Navigation',
    es: 'navegación',
    fr: 'navigation',
    it: 'navigazione',
  },
  dogCommand: {
    en: 'dog command',
    sl: 'pasji ukaz',
    de: 'Hundebefehl',
    es: 'comando de perro',
    fr: 'ordre pour le chien',
    it: 'comando per il cane',
  },
  weather: {
    en: 'current weather',
    sl: 'trenutno vreme',
    de: 'aktuelles Wetter',
    es: 'tiempo actual',
    fr: 'météo actuelle',
    it: 'meteo attuale',
  },
  forecast: {
    en: 'forecast',
    sl: 'napoved',
    de: 'Vorhersage',
    es: 'previsión',
    fr: 'prévisions',
    it: 'previsioni',
  },
  note: {
    en: 'create note',
    sl: 'ustvari beležko',
    de: 'Notiz erstellen',
    es: 'crear nota',
    fr: 'créer une note',
    it: 'creare una nota',
  },
  shoppingList: {
    en: 'shopping list',
    sl: 'nakupovalni seznam',
    de: 'Einkaufsliste',
    es: 'lista de la compra',
    fr: 'liste de courses',
    it: 'lista della spesa',
  },
  namedList: {
    en: 'named list',
    sl: 'imenovan seznam',
    de: 'benannte Liste',
    es: 'lista con nombre',
    fr: 'liste nommée',
    it: 'lista con nome',
  },
  calendarEvent: {
    en: 'calendar event',
    sl: 'dogodek v koledarju',
    de: 'Kalendereintrag',
    es: 'evento de calendario',
    fr: 'événement au calendrier',
    it: 'evento in calendario',
  },
  greet: {
    en: 'greeting',
    sl: 'pozdrav',
    de: 'Begrüßung',
    es: 'saludo',
    fr: 'salutation',
    it: 'saluto',
  },
  thanks: {
    en: 'thanks',
    sl: 'zahvala',
    de: 'Dank',
    es: 'agradecimiento',
    fr: 'remerciement',
    it: 'ringraziamento',
  },
  help: {
    en: 'list all commands',
    sl: 'seznam ukazov',
    de: 'alle Befehle',
    es: 'lista de comandos',
    fr: 'liste des commandes',
    it: 'elenco comandi',
  },
  time: {
    en: 'current time',
    sl: 'trenutni čas',
    de: 'aktuelle Uhrzeit',
    es: 'hora actual',
    fr: 'heure actuelle',
    it: 'ora attuale',
  },
};

interface Command {
  example: string;
  /** Key into `notes`. */
  note: string;
}
interface Section {
  key: string;
  icon: string;
  iconColor: string;
  title: Text;
  commands: Record<LangCode, Command[]>;
}

const sections: Section[] = [
  {
    key: 'greeting',
    icon: 'waving_hand',
    iconColor: 'text-yellow-500',
    title: {
      en: 'Greetings & Small Talk',
      sl: 'Pozdravi in pogovor',
      de: 'Begrüßung & Small Talk',
      es: 'Saludos y conversación',
      fr: 'Salutations et conversation',
      it: 'Saluti e conversazione',
    },
    commands: {
      en: [
        { example: 'Hello', note: 'greet' },
        { example: 'Thanks', note: 'thanks' },
        { example: 'Help', note: 'help' },
        { example: 'What time is it?', note: 'time' },
      ],
      sl: [
        { example: 'Živjo', note: 'greet' },
        { example: 'Hvala', note: 'thanks' },
        { example: 'Pomoč', note: 'help' },
        { example: 'Koliko je ura?', note: 'time' },
      ],
      de: [
        { example: 'Hallo', note: 'greet' },
        { example: 'Danke', note: 'thanks' },
        { example: 'Hilfe', note: 'help' },
        { example: 'Wie spät ist es?', note: 'time' },
      ],
      es: [
        { example: 'Hola', note: 'greet' },
        { example: 'Gracias', note: 'thanks' },
        { example: 'Ayuda', note: 'help' },
        { example: '¿Qué hora es?', note: 'time' },
      ],
      fr: [
        { example: 'Bonjour', note: 'greet' },
        { example: 'Merci', note: 'thanks' },
        { example: 'Aide', note: 'help' },
        { example: 'Quelle heure est-il ?', note: 'time' },
      ],
      it: [
        { example: 'Ciao', note: 'greet' },
        { example: 'Grazie', note: 'thanks' },
        { example: 'Aiuto', note: 'help' },
        { example: 'Che ore sono?', note: 'time' },
      ],
    },
  },
  {
    key: 'device',
    icon: 'lightbulb',
    iconColor: 'text-amber-500',
    title: {
      en: 'Device Control',
      sl: 'Upravljanje naprav',
      de: 'Gerätesteuerung',
      es: 'Control de dispositivos',
      fr: 'Contrôle des appareils',
      it: 'Controllo dispositivi',
    },
    commands: {
      en: [
        { example: 'Turn on the kitchen light', note: 'turnOn' },
        { example: 'Turn off the office ceiling light', note: 'turnOff' },
        { example: 'Set the living room lamp to 50 percent', note: 'setBrightness' },
        { example: 'Brighten the hallway light', note: 'increase' },
        { example: 'Dim the bedroom light', note: 'decrease' },
      ],
      sl: [
        { example: 'Prižgi kuhinjsko luč', note: 'turnOn' },
        { example: 'Ugasni strop v pisarni', note: 'turnOff' },
        { example: 'Nastavi luč v dnevni sobi na 50 odstotkov', note: 'setBrightness' },
        { example: 'Povečaj luč v hodniku', note: 'increase' },
        { example: 'Zatemni luč v spalnici', note: 'decrease' },
      ],
      de: [
        { example: 'Schalte das Küchenlicht ein', note: 'turnOn' },
        { example: 'Schalte das Bürodeckenlicht aus', note: 'turnOff' },
        { example: 'Setze die Wohnzimmerlampe auf 50 Prozent', note: 'setBrightness' },
        { example: 'Erhöhe das Flurlicht', note: 'increase' },
        { example: 'Dimme das Schlafzimmerlicht', note: 'decrease' },
      ],
      es: [
        { example: 'Enciende la luz de la cocina', note: 'turnOn' },
        { example: 'Apaga la luz del techo de la oficina', note: 'turnOff' },
        { example: 'Pon la lámpara del salón al 50 por ciento', note: 'setBrightness' },
        { example: 'Sube la luz del pasillo', note: 'increase' },
        { example: 'Baja la luz del dormitorio', note: 'decrease' },
      ],
      fr: [
        { example: 'Allume la lampe de la cuisine', note: 'turnOn' },
        { example: 'Éteins le plafonnier du bureau', note: 'turnOff' },
        { example: 'Règle la lampe du salon à 50 pour cent', note: 'setBrightness' },
        { example: 'Augmente la lumière du couloir', note: 'increase' },
        { example: 'Baisse la lumière de la chambre', note: 'decrease' },
      ],
      it: [
        { example: 'Accendi la luce della cucina', note: 'turnOn' },
        { example: "Spegni la luce del soffitto dell'ufficio", note: 'turnOff' },
        { example: 'Imposta la luce del soggiorno al 50 per cento', note: 'setBrightness' },
        { example: 'Alza la luce del corridoio', note: 'increase' },
        { example: 'Abbassa la luce della camera', note: 'decrease' },
      ],
    },
  },
  {
    key: 'room_lights',
    icon: 'meeting_room',
    iconColor: 'text-blue-500',
    title: {
      en: 'Room Lights',
      sl: 'Luči v prostoru',
      de: 'Lichter im Raum',
      es: 'Luces de la habitación',
      fr: 'Lumières de la pièce',
      it: 'Luci della stanza',
    },
    commands: {
      en: [
        { example: 'Turn off all lights in the kitchen', note: 'allRoomLights' },
        { example: 'Turn on the lights in the bedroom', note: 'allRoomLights' },
        { example: 'Turn off all lights', note: 'everyLight' },
      ],
      sl: [
        { example: 'Ugasni vse luči v kuhinji', note: 'allRoomLights' },
        { example: 'Prižgi luči v spalnici', note: 'allRoomLights' },
        { example: 'Ugasni vse luči', note: 'everyLight' },
      ],
      de: [
        { example: 'Schalte alle Lichter in der Küche aus', note: 'allRoomLights' },
        { example: 'Schalte alle Lichter im Schlafzimmer ein', note: 'allRoomLights' },
        { example: 'Schalte alle Lichter aus', note: 'everyLight' },
      ],
      es: [
        { example: 'Apaga todas las luces en la cocina', note: 'allRoomLights' },
        { example: 'Enciende las luces en el dormitorio', note: 'allRoomLights' },
        { example: 'Apaga todas las luces', note: 'everyLight' },
      ],
      fr: [
        { example: 'Éteins toutes les lumières dans la cuisine', note: 'allRoomLights' },
        { example: 'Allume les lumières dans la chambre', note: 'allRoomLights' },
        { example: 'Éteins toutes les lumières', note: 'everyLight' },
      ],
      it: [
        { example: 'Spegni tutte le luci in cucina', note: 'allRoomLights' },
        { example: 'Accendi le luci in camera', note: 'allRoomLights' },
        { example: 'Spegni tutte le luci', note: 'everyLight' },
      ],
    },
  },
  {
    key: 'scene',
    icon: 'auto_awesome',
    iconColor: 'text-pink-500',
    title: {
      en: 'Scenes',
      sl: 'Scene',
      de: 'Szenen',
      es: 'Escenas',
      fr: 'Scènes',
      it: 'Scene',
    },
    commands: {
      en: [
        { example: 'Run the Movie scene', note: 'scene' },
        { example: 'Activate Good Morning', note: 'scene' },
        { example: 'List scenes', note: 'sceneList' },
      ],
      sl: [
        { example: 'Zaženi sceno Film', note: 'scene' },
        { example: 'Aktiviraj Dobro jutro', note: 'scene' },
        { example: 'Pokaži scene', note: 'sceneList' },
      ],
      de: [
        { example: 'Starte die Szene Filmabend', note: 'scene' },
        { example: 'Aktiviere Guten Morgen', note: 'scene' },
        { example: 'Zeige Szenen', note: 'sceneList' },
      ],
      es: [
        { example: 'Activa la escena Noche de cine', note: 'scene' },
        { example: 'Ejecuta Buenos días', note: 'scene' },
        { example: 'Muestra escenas', note: 'sceneList' },
      ],
      fr: [
        { example: 'Active la scène Soirée film', note: 'scene' },
        { example: 'Lance Bonjour', note: 'scene' },
        { example: 'Affiche les scènes', note: 'sceneList' },
      ],
      it: [
        { example: 'Attiva la scena Serata film', note: 'scene' },
        { example: 'Esegui Buongiorno', note: 'scene' },
        { example: 'Mostra scene', note: 'sceneList' },
      ],
    },
  },
  {
    key: 'navigate',
    icon: 'route',
    iconColor: 'text-cyan-500',
    title: {
      en: 'UI Navigation',
      sl: 'Navigacija po vmesniku',
      de: 'Navigation',
      es: 'Navegación',
      fr: 'Navigation',
      it: 'Navigazione',
    },
    commands: {
      en: [
        { example: 'Show cameras', note: 'navigate' },
        { example: 'Go to energy', note: 'navigate' },
        { example: 'Open settings', note: 'navigate' },
        { example: 'Show security', note: 'navigate' },
        { example: 'Take me to gallery', note: 'navigate' },
      ],
      sl: [
        { example: 'Pokaži kamere', note: 'navigate' },
        { example: 'Pojdi na energija', note: 'navigate' },
        { example: 'Odpri nastavitve', note: 'navigate' },
        { example: 'Pokaži varnost', note: 'navigate' },
        { example: 'Pojdi na galerija', note: 'navigate' },
      ],
      de: [
        { example: 'Zeige mir die Kameras', note: 'navigate' },
        { example: 'Gehe zu Energie', note: 'navigate' },
        { example: 'Öffne Einstellungen', note: 'navigate' },
        { example: 'Zeige Sicherheit', note: 'navigate' },
        { example: 'Öffne die Galerie', note: 'navigate' },
      ],
      es: [
        { example: 'Muestra las cámaras', note: 'navigate' },
        { example: 'Ve a energía', note: 'navigate' },
        { example: 'Abre ajustes', note: 'navigate' },
        { example: 'Muestra seguridad', note: 'navigate' },
        { example: 'Abre la galería', note: 'navigate' },
      ],
      fr: [
        { example: 'Montre moi les caméras', note: 'navigate' },
        { example: "Ouvre l'énergie", note: 'navigate' },
        { example: 'Ouvre les réglages', note: 'navigate' },
        { example: 'Montre la sécurité', note: 'navigate' },
        { example: 'Va à la galerie', note: 'navigate' },
      ],
      it: [
        { example: 'Mostra le telecamere', note: 'navigate' },
        { example: 'Vai a energia', note: 'navigate' },
        { example: 'Apri impostazioni', note: 'navigate' },
        { example: 'Mostra sicurezza', note: 'navigate' },
        { example: 'Apri la galleria', note: 'navigate' },
      ],
    },
  },
  {
    key: 'dog',
    icon: 'pets',
    iconColor: 'text-orange-500',
    title: {
      en: `Dog Commands (${brand.agentName})`,
      sl: `Pasji ukazi (${brand.agentName})`,
      de: `Hundebefehle (${brand.agentName})`,
      es: `Comandos para el perro (${brand.agentName})`,
      fr: `Ordres pour le chien (${brand.agentName})`,
      it: `Comandi per il cane (${brand.agentName})`,
    },
    commands: {
      en: [
        { example: `${brand.agentName} sit`, note: 'dogCommand' },
        { example: `${brand.agentName} stay`, note: 'dogCommand' },
        { example: `${brand.agentName} lay`, note: 'dogCommand' },
        { example: `${brand.agentName} come`, note: 'dogCommand' },
        { example: `${brand.agentName} shake`, note: 'dogCommand' },
        { example: `${brand.agentName} spin`, note: 'dogCommand' },
        { example: `${brand.agentName} bark`, note: 'dogCommand' },
        { example: `${brand.agentName} dance`, note: 'dogCommand' },
        { example: `${brand.agentName} fetch`, note: 'dogCommand' },
        { example: `${brand.agentName} stand`, note: 'dogCommand' },
      ],
      sl: [
        { example: `${brand.agentName} sedi`, note: 'dogCommand' },
        { example: `${brand.agentName} stoj`, note: 'dogCommand' },
        { example: `${brand.agentName} lezi`, note: 'dogCommand' },
        { example: `${brand.agentName} pridi`, note: 'dogCommand' },
        { example: `${brand.agentName} daj taco`, note: 'dogCommand' },
        { example: `${brand.agentName} obrni se`, note: 'dogCommand' },
        { example: `${brand.agentName} lajaj`, note: 'dogCommand' },
        { example: `${brand.agentName} pleši`, note: 'dogCommand' },
        { example: `${brand.agentName} aport`, note: 'dogCommand' },
        { example: `${brand.agentName} vstani`, note: 'dogCommand' },
      ],
      de: [
        { example: `${brand.agentName} sitz`, note: 'dogCommand' },
        { example: `${brand.agentName} bleib`, note: 'dogCommand' },
        { example: `${brand.agentName} platz`, note: 'dogCommand' },
        { example: `${brand.agentName} komm`, note: 'dogCommand' },
        { example: `${brand.agentName} pfote`, note: 'dogCommand' },
        { example: `${brand.agentName} dreh dich`, note: 'dogCommand' },
        { example: `${brand.agentName} bell`, note: 'dogCommand' },
        { example: `${brand.agentName} tanz`, note: 'dogCommand' },
        { example: `${brand.agentName} bring`, note: 'dogCommand' },
        { example: `${brand.agentName} steh`, note: 'dogCommand' },
      ],
      es: [
        { example: `${brand.agentName} siéntate`, note: 'dogCommand' },
        { example: `${brand.agentName} quieto`, note: 'dogCommand' },
        { example: `${brand.agentName} échate`, note: 'dogCommand' },
        { example: `${brand.agentName} ven`, note: 'dogCommand' },
        { example: `${brand.agentName} da la pata`, note: 'dogCommand' },
        { example: `${brand.agentName} gira`, note: 'dogCommand' },
        { example: `${brand.agentName} ladra`, note: 'dogCommand' },
        { example: `${brand.agentName} baila`, note: 'dogCommand' },
        { example: `${brand.agentName} trae`, note: 'dogCommand' },
        { example: `${brand.agentName} levántate`, note: 'dogCommand' },
      ],
      fr: [
        { example: `${brand.agentName} assis`, note: 'dogCommand' },
        { example: `${brand.agentName} reste`, note: 'dogCommand' },
        { example: `${brand.agentName} couché`, note: 'dogCommand' },
        { example: `${brand.agentName} viens`, note: 'dogCommand' },
        { example: `${brand.agentName} donne la patte`, note: 'dogCommand' },
        { example: `${brand.agentName} tourne`, note: 'dogCommand' },
        { example: `${brand.agentName} aboie`, note: 'dogCommand' },
        { example: `${brand.agentName} danse`, note: 'dogCommand' },
        { example: `${brand.agentName} rapporte`, note: 'dogCommand' },
        { example: `${brand.agentName} debout`, note: 'dogCommand' },
      ],
      it: [
        { example: `${brand.agentName} siedi`, note: 'dogCommand' },
        { example: `${brand.agentName} resta`, note: 'dogCommand' },
        { example: `${brand.agentName} sdraiato`, note: 'dogCommand' },
        { example: `${brand.agentName} vieni`, note: 'dogCommand' },
        { example: `${brand.agentName} dai la zampa`, note: 'dogCommand' },
        { example: `${brand.agentName} gira`, note: 'dogCommand' },
        { example: `${brand.agentName} abbaia`, note: 'dogCommand' },
        { example: `${brand.agentName} balla`, note: 'dogCommand' },
        { example: `${brand.agentName} riporta`, note: 'dogCommand' },
        { example: `${brand.agentName} in piedi`, note: 'dogCommand' },
      ],
    },
  },
  {
    key: 'weather',
    icon: 'cloud',
    iconColor: 'text-sky-500',
    title: {
      en: 'Weather',
      sl: 'Vreme',
      de: 'Wetter',
      es: 'Tiempo',
      fr: 'Météo',
      it: 'Meteo',
    },
    commands: {
      en: [
        { example: "What's the weather?", note: 'weather' },
        { example: 'Weather forecast', note: 'forecast' },
      ],
      sl: [
        { example: 'Kakšno je vreme?', note: 'weather' },
        { example: 'Vremenska napoved', note: 'forecast' },
      ],
      de: [
        { example: 'Wie ist das Wetter?', note: 'weather' },
        { example: 'Wettervorhersage', note: 'forecast' },
      ],
      es: [
        { example: '¿Qué tiempo hace?', note: 'weather' },
        { example: 'El tiempo mañana', note: 'forecast' },
      ],
      fr: [
        { example: 'Quel temps fait-il ?', note: 'weather' },
        { example: 'La météo demain', note: 'forecast' },
      ],
      it: [
        { example: 'Che tempo fa?', note: 'weather' },
        { example: 'Le previsioni di domani', note: 'forecast' },
      ],
    },
  },
  {
    key: 'productivity',
    icon: 'edit_note',
    iconColor: 'text-green-500',
    title: {
      en: 'Notes, Lists & Calendar',
      sl: 'Beležke, seznami in koledar',
      de: 'Notizen, Listen & Kalender',
      es: 'Notas, listas y calendario',
      fr: 'Notes, listes et calendrier',
      it: 'Note, liste e calendario',
    },
    commands: {
      en: [
        { example: 'Note buy milk', note: 'note' },
        { example: 'Add apples to shopping list', note: 'shoppingList' },
        { example: 'Add apples to groceries list', note: 'namedList' },
        { example: 'Add a calendar event tomorrow at 10', note: 'calendarEvent' },
      ],
      sl: [
        { example: 'Zapiši kupi mleko', note: 'note' },
        { example: 'Dodaj jabolka na nakupovalni seznam', note: 'shoppingList' },
        { example: 'Dodaj jabolka na živila', note: 'namedList' },
        { example: 'Dodaj v koledar sestanek jutri ob 10', note: 'calendarEvent' },
      ],
      de: [
        { example: 'Notiere Milch kaufen', note: 'note' },
        { example: 'Setze Milch auf die Einkaufsliste', note: 'shoppingList' },
        { example: 'Setze Äpfel auf die Liste', note: 'namedList' },
        { example: 'Trage einen Termin für morgen ein', note: 'calendarEvent' },
      ],
      es: [
        { example: 'Crea una nota comprar leche', note: 'note' },
        { example: 'Añade leche a la lista de la compra', note: 'shoppingList' },
        { example: 'Añade manzanas a la lista de frutas', note: 'namedList' },
        { example: 'Crea un evento en el calendario mañana', note: 'calendarEvent' },
      ],
      fr: [
        { example: 'Crée une note acheter du lait', note: 'note' },
        { example: 'Ajoute du lait à la liste de courses', note: 'shoppingList' },
        { example: 'Ajoute des pommes à la liste de fruits', note: 'namedList' },
        { example: 'Planifie un rendez-vous demain', note: 'calendarEvent' },
      ],
      it: [
        { example: 'Crea una nota comprare il latte', note: 'note' },
        { example: 'Aggiungi latte alla lista della spesa', note: 'shoppingList' },
        { example: 'Aggiungi mele alla lista della frutta', note: 'namedList' },
        { example: 'Crea un evento in calendario domani', note: 'calendarEvent' },
      ],
    },
  },
];

const visibleSections = computed(() =>
  sections.map((s) => ({
    ...s,
    commands: s.commands[activeLang.value],
  })),
);
</script>
